import { useEffect, useState } from 'react';
import { Linking, Platform } from 'react-native';

import {
	DonationContext,
	donationContextKey,
	notifyDonationCompleted,
} from '../../services/donationsApi';

interface PackageInfo {
	type: string;
	vip?: {
		durationDays: number;
	};
	chips?: number;
	id?: string;
	name?: string;
	price?: number;
	currency?: Array<any>;
	chipAmount?: number;
}

interface PaymentStatusData {
	status: 'success' | 'cancelled' | null;
	sessionId?: string;
	depositId?: string;
	packageInfo?: PackageInfo;
	/** Set when the deposit was a donation: the buyer paid, this player was credited. */
	donation?: DonationContext;
}

/** What a finished web checkout reports, from its tab (`web_payment_result`) or from the page. */
type WebPaymentResult = {
	status: 'success' | 'cancelled';
	depositId: string;
	sessionId?: string;
};

const WEB_PAYMENT_RESULT_EVENT = 'svara:web-payment-result';

/**
 * Reports the result of a checkout run inside this page (Stripe embedded Checkout), the way the
 * checkout tab reports through `web_payment_result`.
 */
export function reportWebPaymentResult(result: WebPaymentResult): void {
	if (typeof window === 'undefined') return;
	window.dispatchEvent(new CustomEvent(WEB_PAYMENT_RESULT_EVENT, { detail: result }));
}

/**
 * Takes the donation context the deposit left next to its package (`packagesSagas`), if any. A
 * successful donation also tells any open profile of the recipient to refresh its chat state.
 */
function takeDonationContext(
	depositId: string | undefined,
	status: PaymentStatusData['status'],
): DonationContext | undefined {
	if (!depositId || typeof localStorage === 'undefined') return undefined;

	try {
		const key = donationContextKey(depositId);
		const saved = localStorage.getItem(key);
		if (!saved) return undefined;
		localStorage.removeItem(key);

		const donation: DonationContext = JSON.parse(saved);
		if (status === 'success') notifyDonationCompleted(donation.recipientPlayerId);
		return donation;
	} catch (e) {
		console.error('Failed to parse donation context:', e);
		return undefined;
	}
}

function parsePaymentUrl(url: string | null): PaymentStatusData | null {
	if (!url) return null;

	// URL в RN може да е: myapp://payment?payment=success&deposit_id=123
	// или https://domain.com/payment?...
	let parsed: URL;
	try {
		parsed = new URL(url);
	} catch {
		// ако URL() гръмне (рядко), няма как да парснем
		return null;
	}

	const params = parsed.searchParams;
	const payment = params.get('payment');
	const sessionId = params.get('session_id') || undefined;
	const depositId = params.get('deposit_id') || undefined;

	if (payment !== 'success' && payment !== 'cancelled') return null;

	const data: PaymentStatusData = {
		status: payment,
		sessionId,
		depositId,
	};

	if (payment === 'success' && depositId) {
		try {
			// Use localStorage directly since this is a synchronous function
			const saved =
				typeof localStorage !== 'undefined' ? localStorage.getItem(`package_${depositId}`) : null;
			if (saved) {
				data.packageInfo = JSON.parse(saved);
				if (typeof localStorage !== 'undefined') {
					localStorage.removeItem(`package_${depositId}`);
				}
			}
		} catch (e) {
			console.error('Failed to parse package info:', e);
		}
	}

	data.donation = takeDonationContext(depositId, payment);

	return data;
}

export function usePaymentStatus(): PaymentStatusData {
	const [paymentData, setPaymentData] = useState<PaymentStatusData>({
		status: null,
	});

	useEffect(() => {
		let sub: any;

		const setFromUrl = (url: string | null) => {
			const parsed = parsePaymentUrl(url);
			if (parsed) setPaymentData(parsed);
		};

		if (Platform.OS === 'web') {
			if (typeof window === 'undefined') return;

			const url = window.location.href;
			setFromUrl(url);

			const parsed = parsePaymentUrl(url);
			if (parsed) {
				const noQueryUrl = window.location.pathname;
				window.history.replaceState({}, '', noQueryUrl);
			}

			// The checkout runs in a popup tab on web. When it completes, that tab stores
			// `web_payment_result` and closes itself; pick the result up here without
			// reloading so the player stays exactly where they are (e.g. at the table).
			const applyResult = (result: WebPaymentResult | null) => {
				try {
					const status: PaymentStatusData['status'] = result?.status ?? null;

					if (status !== 'success' && status !== 'cancelled') return;

					const depositId: string = String(result?.depositId || '');
					let packageInfo: PackageInfo | undefined;

					if (status === 'success' && depositId) {
						const saved = window.localStorage.getItem(`package_${depositId}`);
						if (saved) {
							packageInfo = JSON.parse(saved);
							window.localStorage.removeItem(`package_${depositId}`);
						}
					}

					setPaymentData({
						status,
						depositId,
						sessionId: result?.sessionId || undefined,
						packageInfo,
						donation: takeDonationContext(depositId, status),
					});
				} catch (err) {
					console.error('Failed to handle web payment result:', err);
				}
			};

			const handleStorage = (e: StorageEvent) => {
				if (e.key !== 'web_payment_result') return;
				try {
					const raw = window.localStorage.getItem('web_payment_result');
					if (!raw) return;
					window.localStorage.removeItem('web_payment_result');
					applyResult(JSON.parse(raw));
				} catch (err) {
					console.error('Failed to handle web payment result:', err);
				}
			};
			// A checkout embedded in this very page reports here: `storage` only fires in other tabs.
			const handleEmbedded = (e: Event) => applyResult((e as CustomEvent<WebPaymentResult>).detail);

			window.addEventListener('storage', handleStorage);
			window.addEventListener(WEB_PAYMENT_RESULT_EVENT, handleEmbedded);
			return () => {
				window.removeEventListener('storage', handleStorage);
				window.removeEventListener(WEB_PAYMENT_RESULT_EVENT, handleEmbedded);
			};
		}

		Linking.getInitialURL()
			.then(setFromUrl)
			.catch(() => {});

		sub = Linking.addEventListener('url', e => setFromUrl(e.url));

		return () => {
			sub?.remove?.();
		};
	}, []);

	return paymentData;
}
